// Product Content Management Tab
// Date: January 27, 2025

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contentManagementService, ProductContentSection } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';

const SECTION_KEYS: Array<{ key: ProductContentSection['section_key']; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'features', label: 'Features' },
  { key: 'security', label: 'Security' },
  { key: 'integrations', label: 'Integrations' },
];

export const ProductContentTab: React.FC = () => {
  const [sections, setSections] = useState<ProductContentSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<ProductContentSection['section_key']>('overview');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('1.0.0');

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    const section = sections.find(s => s.section_key === activeSection);
    if (section) {
      setContent(section.content_html);
      setTitle(section.title || '');
      setVersion(section.version);
    } else {
      setContent('');
      setTitle('');
      setVersion('1.0.0');
    }
  }, [activeSection, sections]);

  const loadSections = async () => {
    try {
      setLoading(true);
      const data = await contentManagementService.getAllProductSections();
      setSections(data);
    } catch (error: any) {
      console.error('Error loading product sections:', error);
      toast.error(error.response?.data?.message || 'Failed to load product sections');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim() || !version.trim()) {
      toast.error('Content and version are required');
      return;
    }

    try {
      setSaving(true);
      await contentManagementService.updateProductSection(activeSection, {
        content_html: content,
        version: version,
        title: title || undefined,
      });
      toast.success('Product section saved successfully');
      await loadSections();
    } catch (error: any) {
      console.error('Error saving product section:', error);
      toast.error(error.response?.data?.message || 'Failed to save product section');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-[var(--color-border)]">
        {SECTION_KEYS.map((section) => (
          <button
            key={section.key}
            onClick={() => setActiveSection(section.key)}
            className={`
              px-4 py-2 text-sm font-medium border-b-2 transition-colors
              ${activeSection === section.key
                ? 'border-[var(--color-accent-bg)] text-[var(--color-surface-text)] font-semibold'
                : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]'
              }
            `}
          >
            {section.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit {SECTION_KEYS.find(s => s.key === activeSection)?.label} Section</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Version
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.0.0"
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Content (HTML)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] font-mono text-sm"
              placeholder="Enter HTML content..."
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            {saving ? 'Saving...' : 'Save Section'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

